/**
 * Convert a string to camelCase
 * @param string - The string to convert
 * @returns The camelCase string
 * @example camelCase('hello-world') // 'helloWorld'
 */
export function camelCase (string: string): string {
  return string
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, char) => char.toUpperCase())
}

/**
 * Capitalize the first letter of a string
 * @param string - The string to capitalize
 * @returns The capitalized string
 * @example capitalize('hello world') // 'Hello world'
 */
export function capitalize (string: string): string {
  if (!string) return string
  return string.charAt(0).toUpperCase() + string.slice(1)
}

/**
 * Remove extra whitespace from a string
 * @param string - The string to clean
 * @returns The string with normalized whitespace
 * @example cleanWhitespace('  hello   world  ') // 'hello world'
 */
export function cleanWhitespace (string: string): string {
  return string.trim().replace(/\s+/g, ' ')
}

/**
 * Extract strings inside braces (braces included)
 * @param string - The string to extract the strings from
 * @param braces - The braces to extract the strings from
 * @returns The strings inside the braces (braces included)
 * @example extractStringInside('{YEAR}', ['{', '}']) // '{YEAR}'
 */
export function extractStringInside (string: string, braces = ['{', '}']): string {
  return string.match(new RegExp(`${braces[0]}[^${braces[1]}]+${braces[1]}`, 'g'))?.[0] ?? ''
}

/**
 * Check if a string is a valid email address
 * @param string - The string to validate
 * @returns True if the string is a valid email
 * @example isEmail('test@example.com') // true
 */
export function isEmail (string: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(string)
}

/**
 * Check if a string is a valid URL
 * @param string - The string to validate
 * @returns True if the string is a valid URL
 * @example isUrl('https://example.com') // true
 */
export function isUrl (string: string): boolean {
  try {
    const url = new URL(string)
    return !!url
  } catch {
    return false
  }
}

/**
 * Convert a string to kebab-case
 * @param string - The string to convert
 * @returns The kebab-case string
 * @example kebabCase('helloWorld') // 'hello-world'
 */
export function kebabCase (string: string): string {
  return string
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase()
}

/**
 * Extract all tags and their content from a string with BBCode-like tags
 * @param string - The string containing BBCode-like tags
 * @returns Array of objects with tag name, content, and full match
 * @example parseBBCodeTags('Hello [tw]my word[/tw] and [hw]world[/hw]') // [{ tag: 'tw', content: 'my word', match: '[tw]my word[/tw]' }, { tag: 'hw', content: 'world', match: '[hw]world[/hw]' }]
 */
export function parseBBCodeTags (string: string): Array<{ tag: string, content: string, match: string }> {
  const regex = /\\?\[([^\]\\]+)\\?\]([^[/]+?)\\?\[\/\1\\?\]/g
  const matches: Array<{ tag: string, content: string, match: string }> = []
  let match = null

  while ((match = regex.exec(string)) !== null) {
    matches.push({
      tag: match[1],
      content: match[2],
      match: match[0]
    })
  }

  return matches
}

/**
 * Convert a string to PascalCase
 * @param string - The string to convert
 * @returns The PascalCase string
 * @example pascalCase('hello-world') // 'HelloWorld'
 */
export function pascalCase (string: string): string {
  return camelCase(string).replace(/^./, (char) => char.toUpperCase())
}

/**
 * Generate a random string
 * @param length - Length of the random string
 * @param charset - Character set to use (default: alphanumeric)
 * @returns A random string
 * @example randomString(8) // 'aB3xY9mK'
 */
export function randomString (length: number, charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'): string {
  let result = ''
  for (let i = 0; i < length; i++) {
    result += charset.charAt(Math.floor(Math.random() * charset.length))
  }
  return result
}

/**
 * Remove accented characters from a string
 * @param string - The string to normalize
 * @returns The string without accented characters
 * @example removeAccents('café') // 'cafe'
 */
export function removeAccents (string: string): string {
  return string.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

/**
 * Replace strings with another string
 * @param string - The string to replace the strings from
 * @param search - The strings to replace
 * @param replace - The replacement string
 * @returns The string with the replaced strings
 * @example replaceStrings('Hello {NAME}', ['{NAME}'], 'World') // 'Hello World'
 */
export function replaceStrings (string: string, search: string | string[], replace: string): string {
  return Array.isArray(search)
    ? search.reduce((acc, string) => acc.replace(string, replace), string)
    : string.replace(search, replace)
}

/**
 * Reverse a string
 * @param string - The string to reverse
 * @returns The reversed string
 * @example reverse('hello') // 'olleh'
 */
export function reverse (string: string): string {
  return string.split('').reverse().join('')
}

/**
 * Convert a string to URL-friendly slug format
 * @param string - The string to slugify
 * @returns The slugified string
 * @example slugify('Hello World!') // 'hello-world'
 */
export function slugify (string: string): string {
  const temp = removeAccents(string)
  return temp
    .toLowerCase()
    .trim()
    // Replace any non-alphanumeric characters with hyphens
    .replace(/[^a-z0-9]+/g, '-')
    // Remove consecutive hyphens
    .replace(/-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '')
}

/**
 * Convert a string to snake_case
 * @param string - The string to convert
 * @returns The snake_case string
 * @example snakeCase('helloWorld') // 'hello_world'
 */
export function snakeCase (string: string): string {
  return string
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase()
}

/**
 * Remove HTML tags from a string
 * @param string - The string containing HTML
 * @returns The string without HTML tags and the tag
 * @example stripHtml('<p>Hello <strong>World</strong></p>') // 'Hello World'
 */
export function stripHtml (string: string): { string: string, tag: string } {
  return {
    string: string.replace(/<[^>]*>/g, ''),
    tag: string.match(/<[^>]*>/g)?.[0]
  }
}
